import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, type FieldError } from 'react-hook-form';

import type { ExternalCourseFormSchema } from './schema';
import { AutocompleteStatesAndCities } from '@/components/partial/autocomplete-states-cities';
import { MoneyInput } from '@/components/shared/money-input';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { ModalityTypes, PriceTypes } from '@/models/catalog/course';

const getErrorMessage = (error: FieldError | undefined): string | undefined =>
  error?.message;

export const CourseForm = () => {
  const { bootstrap } = useBootstrapData();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useFormContext<ExternalCourseFormSchema>();

  const saleType = watch('sale_type');
  const modality = watch('modality');
  const disableDuration = watch('disable_duration');

  return (
    <>
      <Controller
        control={control}
        name="institution"
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!errors.institution}>
            <Stack spacing={1}>
              <FormLabel htmlFor="institution">Instituição</FormLabel>
              <TextField
                {...field}
                id="institution"
                error={!!error}
                helperText={getErrorMessage(error)}
                placeholder="Digite o nome da instituição"
              />
            </Stack>
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="course"
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!errors.course}>
            <Stack spacing={1}>
              <FormLabel htmlFor="course">Curso</FormLabel>
              <TextField
                {...field}
                id="course"
                error={!!error}
                helperText={getErrorMessage(error)}
                placeholder="Digite o nome do curso"
              />
            </Stack>
          </FormControl>
        )}
      />
      <Stack direction={{ md: 'row' }} gap={2}>
        <Controller
          control={control}
          name="course_type"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} fullWidth>
              <Stack spacing={1}>
                <FormLabel htmlFor="course_type">Categoria</FormLabel>
                <Autocomplete
                  {...field}
                  id="course_type"
                  options={bootstrap?.taxonomy?.course_types ?? []}
                  getOptionLabel={(option) => option.label}
                  getOptionKey={(option) => option.type}
                  onChange={(_, value) => field.onChange(value?.type)}
                  value={
                    bootstrap?.taxonomy?.course_types?.find(
                      (option) => option.type === field.value
                    ) ?? null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione..."
                      error={!!error}
                      helperText={getErrorMessage(error)}
                    />
                  )}
                />
              </Stack>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="modality"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} fullWidth>
              <Stack spacing={1}>
                <FormLabel htmlFor="modality">Modalidade</FormLabel>
                <Autocomplete
                  {...field}
                  id="modality"
                  options={bootstrap?.taxonomy?.modalities ?? []}
                  getOptionLabel={(option) => option.name}
                  getOptionKey={(option) => option.value}
                  onChange={(_, value) => field.onChange(value?.value)}
                  value={
                    bootstrap?.taxonomy?.modalities?.find(
                      (option) => option.value === field.value
                    ) ?? null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione..."
                      error={!!error}
                      helperText={getErrorMessage(error)}
                    />
                  )}
                />
              </Stack>
            </FormControl>
          )}
        />
      </Stack>
      {modality &&
        [ModalityTypes.Hybrid, ModalityTypes.InPerson].includes(modality) && (
          <>
            <AutocompleteStatesAndCities
              onStateAndCityChange={(state, city) => {
                setValue('state', state);
                setValue('city', city);
              }}
              initialState={watch('state')}
              initialCity={watch('city')}
              stateError={getErrorMessage(errors.state)}
              cityError={getErrorMessage(errors.city)}
            />
            <Stack direction={{ md: 'row' }} gap={2}>
              <Controller
                control={control}
                name="period"
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={!!error} fullWidth>
                    <Stack spacing={1}>
                      <FormLabel htmlFor="period">Turno</FormLabel>
                      <Autocomplete
                        {...field}
                        id="period"
                        options={bootstrap?.taxonomy?.periods ?? []}
                        getOptionLabel={(option) => option.name}
                        getOptionKey={(option) => option.value}
                        onChange={(_, value) => field.onChange(value?.value)}
                        value={
                          bootstrap?.taxonomy?.periods?.find(
                            (option) => option.value === field.value
                          ) ?? null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione..."
                            error={!!error}
                            helperText={getErrorMessage(error)}
                          />
                        )}
                      />
                    </Stack>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="campus"
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={!!errors.campus} fullWidth>
                    <Stack spacing={1}>
                      <FormLabel htmlFor="campus">Polo</FormLabel>
                      <TextField
                        {...field}
                        id="campus"
                        error={!!error}
                        helperText={getErrorMessage(error)}
                        placeholder="Digite o nome do polo"
                      />
                    </Stack>
                  </FormControl>
                )}
              />
            </Stack>
          </>
        )}

      <Stack direction={{ md: 'row' }} gap={2}>
        <Controller
          control={control}
          name="workload"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!errors.workload} sx={{ flex: 1 }}>
              <Stack spacing={1}>
                <FormLabel htmlFor="workload">Carga horária semanal</FormLabel>
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? null : Number(value));
                  }}
                  id="workload"
                  error={!!error}
                  helperText={getErrorMessage(error)}
                  type="number"
                  placeholder="Ex.: 360"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment
                          disableTypography
                          position="end"
                          sx={{ mr: 0.5 }}
                        >
                          horas semanais
                        </InputAdornment>
                      ),
                    },

                    htmlInput: { 'data-testid': 'workload-input' },
                  }}
                />
              </Stack>
            </FormControl>
          )}
        />
        <Box display={{ xs: 'none', md: 'block' }} flex={1} />
      </Stack>
      <Stack direction="row" flexWrap="wrap" alignItems="start" gap={2}>
        <Controller
          control={control}
          name="duration"
          render={({ field }) => (
            <FormControl
              error={!!errors.duration}
              disabled={disableDuration ?? false}
              sx={{ flex: 1, maxWidth: { md: 390 }, minWidth: 100 }}
            >
              <Stack spacing={1}>
                <FormLabel htmlFor="duration">Duração</FormLabel>
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? null : Number(value));
                  }}
                  id="duration"
                  error={!!errors.duration}
                  helperText={getErrorMessage(errors.duration)}
                  type="number"
                  placeholder="Ex.: 1, 3, 6, 10"
                  disabled={disableDuration ?? false}
                />
              </Stack>
            </FormControl>
          )}
        />
        <Stack direction="row" alignItems="start">
          <Controller
            control={control}
            name="duration_type"
            render={({ field }) => (
              <FormControl
                error={!!errors.duration_type}
                disabled={disableDuration ?? false}
              >
                <Stack spacing={1}>
                  <FormLabel htmlFor="duration_type">Tipo</FormLabel>
                  <Autocomplete
                    {...field}
                    id="duration_type"
                    options={bootstrap?.taxonomy?.durations ?? []}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.value}
                    onChange={(_, value) => field.onChange(value?.value)}
                    value={
                      bootstrap?.taxonomy?.durations?.find(
                        (option) => option.value === field.value
                      ) ?? null
                    }
                    disabled={disableDuration ?? false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Selecione"
                        sx={{ width: 130 }}
                        error={!!errors.duration_type}
                        helperText={getErrorMessage(errors.duration_type)}
                      />
                    )}
                  />
                </Stack>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="disable_duration"
            render={({ field }) => (
              <Stack gap={{ xs: 0, sm: 1.5 }} ml={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value ?? false}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          setValue('duration', null);
                          setValue('duration_type', '');
                        }
                        clearErrors(['duration', 'duration_type']);
                      }}
                      sx={{ mt: 3.5 }}
                    />
                  }
                  label={<Typography mt={3.5}>Sem duração</Typography>}
                />
                <FormHelperText>&nbsp;</FormHelperText>
              </Stack>
            )}
          />
        </Stack>
      </Stack>
      <Controller
        control={control}
        name="sale_type"
        render={({ field }) => (
          <FormControl component="fieldset">
            <FormLabel component="legend" id="sale_type-label">
              Qual é o formato de pagamento do item?
            </FormLabel>
            <RadioGroup {...field} aria-labelledby="sale_type-label">
              <FormControlLabel
                control={<Radio size="small" />}
                label={
                  <Typography>
                    Compra (pagamento à vista ou parcelado)
                  </Typography>
                }
                value={PriceTypes.OneOff}
              />
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>Mensalidade</Typography>}
                value={PriceTypes.Tuition}
              />
            </RadioGroup>
          </FormControl>
        )}
      />
      <Stack direction={{ md: 'row' }} gap={2}>
        <Controller
          control={control}
          name="amount"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!errors.amount} sx={{ flex: 1 }}>
              <Stack spacing={1}>
                <FormLabel htmlFor="amount">
                  {saleType === 'tuition'
                    ? 'Valor da mensalidade'
                    : 'Custo total do item'}{' '}
                  (R$)
                </FormLabel>
                <TextField
                  {...field}
                  id="amount"
                  error={!!error}
                  helperText={getErrorMessage(error)}
                  slotProps={{
                    input: {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      inputComponent: MoneyInput as any,
                      startAdornment: (
                        <InputAdornment disableTypography position="start">
                          R$
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Stack>
            </FormControl>
          )}
        />
        <Box display={{ xs: 'none', md: 'block' }} flex={1} />
      </Stack>
      <Controller
        control={control}
        name="link"
        render={({ field, fieldState: { error } }) => (
          <Stack spacing={1}>
            <FormLabel htmlFor="link">
              Link do item
              <Box
                component="span"
                sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
              >
                opcional
              </Box>
            </FormLabel>
            <TextField
              {...field}
              id="link"
              error={!!error}
              helperText={getErrorMessage(error)}
              placeholder="Ex.: www.link.com"
            />
          </Stack>
        )}
      />

      <Controller
        control={control}
        name="request_reason"
        render={({ field, fieldState: { error } }) => (
          <Stack spacing={1}>
            <FormLabel htmlFor="request_reason">
              Descrição ou justificativa
              <Box
                component="span"
                sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
              >
                opcional
              </Box>
            </FormLabel>
            <TextField
              {...field}
              id="request_reason"
              error={!!error}
              helperText={getErrorMessage(error)}
              multiline
              rows={4}
              placeholder="Descreva o motivo da compra para avaliação da sua empresa."
            />
          </Stack>
        )}
      />
    </>
  );
};
